import { DEFAULT_AGENT_GROUNDING } from '@/constants/agent/defaults';
import { getDefaultTemperature } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import styled from '@emotion/styled';
import debounce from 'lodash/debounce';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { Divider, StyledSlider, Title } from '../../common';
import { New } from '../../common/banners/new';
import { CreativityLevelTooltip } from './InstructionsTooltip';

const UPDATE_STORE_DEBOUNCE_TIMEOUT_MS = 400;

const Container = styled.div`
    display: flex;
    flex-direction: column;

    width: 100%;
    padding: 12px;

    border: 1px solid ${theme.colors.secondary[200]};
    border-radius: 8px;
`;

const TopPartContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    margin-bottom: 16px;
`;

const StyledTitleContainer = styled(Title)`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    gap: 4px;
`;

const StyledTitle = styled(Title)`
    font-size: 14px;
`;

const StyledDivider = styled(Divider)`
    color: ${theme.colors.grey.border};
`;

const BottomPartContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    gap: 16px;

    /* padding: 12px; */
    margin-top: 12px;
`;

const SliderIndicator = styled.text`
    color: ${theme.colors.secondary[500]};
    text-align: start;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: -0.24px;
`;

export const CreativityLevel = observer(() => {
    const agentEditorStore = useAgentEditorStore();

    const [temperature, setTemperature] = useState(
        (agentEditorStore.llm.temperature ?? getDefaultTemperature(agentEditorStore.llm.template ?? DEFAULT_AGENT_GROUNDING)) * 100
    );

    useEffect(() => {
        if (!agentEditorStore.llm.temperature) return;
        setTemperature(agentEditorStore.llm.temperature * 100);
    }, [agentEditorStore.llm.temperature]);

    const debouncedUpdateStore = useCallback(
        debounce(
            (temperature: number) => agentEditorStore.setLLM(llm => ({ ...llm, temperature: (temperature as number) / 100 })),
            UPDATE_STORE_DEBOUNCE_TIMEOUT_MS
        ),
        []
    );

    const onChange = (_e: Event, value: number | number[]) => {
        setTemperature(value as number);
        debouncedUpdateStore(value as number);
    };

    return (
        <Container>
            <TopPartContainer>
                <StyledTitleContainer>
                    <StyledTitle>Adjust creativity level</StyledTitle>
                    <CreativityLevelTooltip />
                    <New />
                </StyledTitleContainer>
            </TopPartContainer>
            <StyledDivider />
            <BottomPartContainer>
                <SliderIndicator>More predictable and focused</SliderIndicator>
                <StyledSlider min={0} max={100} step={5} value={temperature} onChange={onChange} valueLabelDisplay="auto" />
                <SliderIndicator>More diverse and creative</SliderIndicator>
            </BottomPartContainer>
        </Container>
    );
});
